import BaseService from "./BaseService";
import { playSound, soundDurationMs, type SoundName } from "./sounds";

export type Sound = SoundName;

export class SoundService extends BaseService {
  private static instance: SoundService | null = null;
  private loopTimer: ReturnType<typeof setInterval> | null = null;
  private loopRemaining: number | null = null;

  /**
   * Get the singleton instance of SoundService
   */
  static getInstance(): SoundService {
    if (!SoundService.instance) {
      SoundService.instance = new SoundService();
    }
    return SoundService.instance;
  }

  /**
   * Clear the singleton instance (useful for testing)
   */
  static clearInstance(): void {
    SoundService.instance?.stop();
    SoundService.instance = null;
  }

  /**
   * Play a synthesized sound.
   * @param sound The sound to play
   * @param loop Whether to loop (true = infinite, number = that many plays)
   */
  play(sound: Sound, loop: boolean | number = false) {
    this.stop();

    playSound(sound);

    if (loop === true) {
      this.loopRemaining = null;
      this.startLoop(sound);
    } else if (typeof loop === "number" && loop > 1) {
      this.loopRemaining = loop - 1;
      this.startLoop(sound);
    }
  }

  /**
   * Stop looping. The beep already in flight finishes on its own envelope.
   */
  stop() {
    if (this.loopTimer !== null) {
      clearInterval(this.loopTimer);
      this.loopTimer = null;
    }
    this.loopRemaining = null;
  }

  private startLoop(sound: Sound) {
    const intervalMs = soundDurationMs(sound);
    this.loopTimer = setInterval(() => {
      if (this.loopRemaining !== null) {
        if (this.loopRemaining <= 0) {
          this.stop();
          return;
        }
        this.loopRemaining -= 1;
      }
      playSound(sound);
    }, intervalMs);
  }
}

export const soundService = SoundService.getInstance();
export default soundService;
